import { createSlice } from '@reduxjs/toolkit';
import { notifyUserError, notifyUserSuccess } from 'utils/notifications';

import {
  deleteMedicalVisits,
  getMedicalVisits,
  getMedicalVisitsPreview,
  getMedicalVisitsTypes,
  getMedicalVisitsStatuses,
  postMedicalVisits,
  putMedicalVisits,
  MedicalVisitsItem,
  MedicalVisitsFilters
} from './medicalVisitsActions';

type SelectItem = {
  id: number;
  name: string;
};

type InitialState = {
  medicalVisitsTypes: SelectItem[];
  medicalVisitsStatuses: SelectItem[];
  medicalVisitsList: {
    content: MedicalVisitsItem[];
    page: number;
    size: number;
    sorts: { title: string; direction: string }[] | null;
    filters: MedicalVisitsFilters | null;
  };
  medicalVisitsListLoading: boolean;
  medicalVisitsPreviewList: Pick<
    MedicalVisitsItem,
    'id' | 'location' | 'dateOfEncounter' | 'dateAndTimeOfEncounter'
  >[];
  medicalVisitsPreviewListLoading: boolean;
};

const initialState: InitialState = {
  medicalVisitsPreviewList: [],
  medicalVisitsPreviewListLoading: false,
  medicalVisitsTypes: [],
  medicalVisitsStatuses: [],
  medicalVisitsList: {
    content: [],
    page: 0,
    size: 99999,
    sorts: null,
    filters: null
  },
  medicalVisitsListLoading: false
};

export const medicalVisitsSlice = createSlice({
  name: 'medicalVisitsSlice',
  initialState,
  reducers: {
    updateSortsList: (state, action) => {
      state.medicalVisitsList.sorts = action.payload;
    },
    resetMedicalVisitsList: (state) => {
      state.medicalVisitsTypes = [];
      state.medicalVisitsStatuses = [];
      state.medicalVisitsListLoading = false;
      state.medicalVisitsList = {
        content: [],
        page: 0,
        size: 99999,
        sorts: null,
        filters: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMedicalVisitsPreview.pending, (state, action) => {
        state.medicalVisitsPreviewListLoading = true;
      })
      .addCase(getMedicalVisitsPreview.fulfilled, (state, action) => {
        state.medicalVisitsPreviewList = action.payload.data;
        state.medicalVisitsPreviewListLoading = false;
      })
      .addCase(getMedicalVisitsPreview.rejected, (state, { payload }: any) => {
        state.medicalVisitsPreviewListLoading = false;
        notifyUserError(payload);
      })
      .addCase(getMedicalVisits.pending, (state, action) => {
        state.medicalVisitsListLoading = true;
      })
      .addCase(getMedicalVisits.fulfilled, (state, action) => {
        state.medicalVisitsList = action.payload;
        state.medicalVisitsListLoading = false;
      })
      .addCase(getMedicalVisits.rejected, (state, { payload }: any) => {
        state.medicalVisitsListLoading = false;
        notifyUserError(payload);
      })
      .addCase(postMedicalVisits.fulfilled, (state, { payload }: any) => {
        notifyUserSuccess('Your encounter was saved');
      })
      .addCase(postMedicalVisits.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putMedicalVisits.fulfilled, (state, { payload }: any) => {
        const index = state.medicalVisitsList.content.findIndex((a) => a.id === payload.data?.id);
        const type = state.medicalVisitsTypes.find((t) => t.id === payload.data?.typeId)?.name;
        const status = state.medicalVisitsStatuses.find(
          (s) => s.id === payload.data?.statusId
        )?.name;
        if (index !== -1) {
          state.medicalVisitsList.content[index] = { ...payload.data, type, status };
        }
      })
      .addCase(putMedicalVisits.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(deleteMedicalVisits.fulfilled, (state, { payload }: any) => {
        const index = state.medicalVisitsList.content.findIndex((a) => a.id === payload);
        if (index !== -1) {
          state.medicalVisitsList.content.splice(index, 1);
        }
      })
      .addCase(deleteMedicalVisits.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getMedicalVisitsTypes.fulfilled, (state, action) => {
        state.medicalVisitsTypes = action.payload;
      })
      .addCase(getMedicalVisitsTypes.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getMedicalVisitsStatuses.fulfilled, (state, action) => {
        state.medicalVisitsStatuses = action.payload;
      })
      .addCase(getMedicalVisitsStatuses.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      });
  }
});

export const { updateSortsList, resetMedicalVisitsList } = medicalVisitsSlice.actions;
export default medicalVisitsSlice.reducer;
